import { useState, useCallback } from 'react';
import axios from 'axios';

import { BACKEND_URL } from '../utils/api';
import { createThumbnail } from '../utils/thumbnail';
import { invalidateMatching } from '../utils/profileCache';

export const useUpload = ({ category, onSuccess, onClose }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState('');

  const uploadSingle = useCallback(async ({ fileBlob, fileName, mediaType, note, vibeDate }) => {
    if (!fileBlob || !note.trim()) {
      setError('Please add media and a note');
      return false;
    }
    setUploading(true);
    setUploadPercent(0);
    setError('');
    try {
      const formData = new FormData();
      formData.append('category', category);
      formData.append('note', note);
      formData.append('media_type', mediaType);
      formData.append('vibe_date', vibeDate);
      const ext = fileName?.includes('.') ? fileName.split('.').pop() : (mediaType === 'image' ? 'jpg' : 'webm');
      formData.append('file', fileBlob, `vibe.${ext}`);

      // Zero-cost thumbnail (browser-side canvas resize, ~30KB JPEG).
      // Only generated for images — videos/audio are handled by the
      // gallery's poster/icon UI and don't benefit from a thumb.
      if (mediaType === 'image') {
        const thumbBlob = await createThumbnail(fileBlob);
        if (thumbBlob) formData.append('thumbnail', thumbBlob, 'thumb.jpg');
      }

      await axios.post(`${BACKEND_URL}/api/memories`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
        onUploadProgress: (e) => {
          if (e.total) setUploadPercent(Math.round((e.loaded / e.total) * 100));
        },
      });
      setUploadPercent(100);

      // Invalidate cached gallery responses so the new vibe appears on
      // next profile visit.
      invalidateMatching('/api/users/');
      invalidateMatching('/api/memories');

      // No forced delay — onTransitionEnd of the CSS animation handles timing.
      // onSuccess marks the upload as done; the hunter transition end fires the celebration.
      onSuccess();
      onClose();
      return true;
    } catch (err) {
      const msg = err.code === 'ECONNABORTED'
        ? 'Upload timed out. Try a shorter clip or better connection.'
        : err.response?.data?.detail || err.message || 'Upload failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setUploading(false);
      setUploadPercent(0);
      return false;
    }
  }, [category, onSuccess, onClose]);

  const uploadBatch = useCallback(async ({ fileQueue, note, vibeDate }) => {
    if (fileQueue.length === 0 || !note.trim()) {
      setError('Please add a note for your vibes');
      return false;
    }
    setUploading(true);
    setError('');
    setUploadProgress({ current: 0, total: fileQueue.length });
    let failed = 0;
    for (let i = 0; i < fileQueue.length; i++) {
      setUploadProgress({ current: i + 1, total: fileQueue.length });
      setUploadPercent(0);
      try {
        const f = fileQueue[i];
        const type = f.type.startsWith('image') ? 'image' : f.type.startsWith('video') ? 'video' : 'audio';
        const formData = new FormData();
        formData.append('category', category);
        formData.append('note', note);
        formData.append('media_type', type);
        formData.append('vibe_date', vibeDate);
        formData.append('file', f, f.name);
        if (type === 'image') {
          const thumbBlob = await createThumbnail(f);
          if (thumbBlob) formData.append('thumbnail', thumbBlob, 'thumb.jpg');
        }
        await axios.post(`${BACKEND_URL}/api/memories`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 600000,
          onUploadProgress: (e) => {
            if (e.total) setUploadPercent(Math.round((e.loaded / e.total) * 100));
          },
        });
      } catch (err) {
        failed++;
      }
    }
    setUploading(false);
    setUploadPercent(0);
    if (failed > 0) {
      setError(`${failed} of ${fileQueue.length} uploads failed`);
      return false;
    }
    invalidateMatching('/api/users/');
    invalidateMatching('/api/memories');
    onSuccess();
    onClose();
    return true;
  }, [category, onSuccess, onClose]);

  return { uploading, uploadPercent, uploadProgress, error, setError, uploadSingle, uploadBatch };
};
